import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { certificationsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Search,
  Award,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
  Building,
  Download,
  Upload,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

export default function Certifications() {
  const { user } = useAuth();
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const fileInputRef = React.useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    issuing_org: '',
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchCertifications();
  }, []);

  const fetchCertifications = async () => {
    try {
      const response = await certificationsAPI.getAll();
      setCertifications(response.data);
    } catch (error) {
      console.error('Failed to fetch certifications:', error);
      toast.error('Failed to load certifications');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (cert = null) => {
    if (cert) {
      setEditingCert(cert);
      setFormData({
        name: cert.name || '',
        description: cert.description || '',
        issuing_org: cert.issuing_org || '',
      });
    } else {
      setEditingCert(null);
      setFormData({
        name: '',
        description: '',
        issuing_org: '',
      });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCert(null);
    setFormData({
      name: '',
      description: '',
      issuing_org: '',
    });
  };

  const handleExportCSV = async () => {
    try {
      await certificationsAPI.exportCSV();
      toast.success('Certifications exported successfully');
    } catch (error) {
      toast.error('Failed to export certifications');
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    try {
      const text = await file.text();
      const response = await certificationsAPI.importCSV(text);
      const result = response.data;
      
      if (result.errors?.length > 0) {
        toast.warning(`Imported ${result.imported} certifications. ${result.errors.length} errors occurred.`);
        console.log('Import errors:', result.errors);
      } else {
        toast.success(result.message);
      }
      fetchCertifications();
      setShowImportDialog(false);
    } catch (error) {
      console.error('Import error:', error.response?.data);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to import certifications';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      toast.error(errorMessage);
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Certification name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingCert) {
        await certificationsAPI.update(editingCert.id, formData);
        toast.success('Certification updated successfully');
      } else {
        await certificationsAPI.create(formData);
        toast.success('Certification created successfully');
      }
      handleCloseForm();
      fetchCertifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save certification');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await certificationsAPI.delete(deleteId);
      toast.success('Certification deleted successfully');
      fetchCertifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete certification');
    } finally {
      setDeleteId(null);
    }
  };

  const filteredCerts = certifications.filter((cert) =>
    cert.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (cert.issuing_org && cert.issuing_org.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!isAdmin) {
      return <div className="p-8 text-center text-muted-foreground">Access Denied</div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Certifications</h1>
          <p className="text-muted-foreground mt-1">
            Manage certifications and qualifications ({certifications.length} total)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-certifications-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-certifications-btn">
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          <Button onClick={() => handleOpenForm()} data-testid="add-certification-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Certification
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search certifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Certifications Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
            </div>
          ) : filteredCerts.length === 0 ? (
            <div className="p-12 text-center">
              <Award className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No certifications found</p>
              <Button className="mt-4" onClick={() => handleOpenForm()}>
                <Plus className="w-4 h-4 mr-2" />
                Add your first certification
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Issuing Org</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="hidden lg:table-cell">Created</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCerts.map((cert) => (
                  <TableRow key={cert.id} className="table-row-hover">
                    <TableCell>
                      <div className="font-medium">{cert.name}</div>
                    </TableCell>
                    <TableCell>
                      {cert.issuing_org && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Building className="w-3.5 h-3.5" />
                          {cert.issuing_org}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground truncate max-w-xs block">
                        {cert.description}
                      </span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-sm text-muted-foreground">
                        {format(parseISO(cert.created_at), 'MMM d, yyyy')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenForm(cert)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteId(cert.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Certification Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCert ? 'Edit Certification' : 'Add New Certification'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Certification Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Certified Safety Specialist"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="issuing_org">Issuing Organization</Label>
              <Input
                id="issuing_org"
                value={formData.issuing_org}
                onChange={(e) => setFormData(prev => ({ ...prev, issuing_org: e.target.value }))}
                placeholder="Safety Board"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Details about the certification..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingCert ? (
                  'Update Certification'
                ) : (
                  'Add Certification'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Certification</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this certification?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete Certification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Certifications from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file with the following columns: Name, Description, Issuing Organization
            </p>
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
                id="cert-csv-upload"
              />
              <label htmlFor="cert-csv-upload" className="cursor-pointer">
                <span className="text-primary hover:underline">Click to upload</span>
                <span className="text-muted-foreground"> or drag and drop</span>
              </label>
              <p className="text-xs text-muted-foreground mt-1">CSV files only</p>
            </div>
            {importLoading && (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Importing...</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
